import { Spinner, theme } from '@zappyrent/hundop';
import { AxiosError } from 'axios';
import { upperFirst } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import { BookingListItem } from '../../components/bookings';
import Filter from '../../components/Filter';
import { MainLayout } from '../../components/MainLayout';
import { Collapsible } from '../../components/UI/Collapsible';
import bookingsAPI from '../../services/api/bookings';
import { useIntersectionObserver, useUser } from '../../shared/hooks';
import useStore from '../../store';
import { LandlordBooking, TenantBooking } from '../../types/bookings';
import { PaginatedResponse } from '../../types/utils';
import {
  StyledEmptyList,
  StyledList,
  StyledListWrapper,
  StyledSpinner,
  StyledTitle,
  StyledTitleWrapper,
} from './Bookings.styles';

export function Bookings(): JSX.Element | null {
  const per_page = 30;
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const { t } = useTranslation(['booking']);
  const isLoadingUser = useUser();
  const { user } = useStore(state => ({
    user: state.user,
  }));
  const [bookings, setBookings] = useState<LandlordBooking[] | TenantBooking[]>(
    [],
  );
  const [bookingsLoading, setBookingLoading] = useState<boolean>(false);
  const [bookingsStatusFilters, setBookingsStatusFilters] =
    useState<string>('');
  const search = useLocation().search;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  useEffect(() => {
    if (searchParams.has('id') && !!bookings.length && !isScrolled) {
      if (
        (bookings as LandlordBooking[]).find(
          booking => booking.id === searchParams?.get('id'),
        )
      ) {
        setTimeout(() => {
          document
            ?.getElementById(searchParams?.get('id') as string)
            ?.scrollIntoView({
              behavior: 'smooth',
              inline: 'center',
              block: 'start',
            });
          setIsScrolled(true);
        }, 100);
      } else {
        fetchNextPage();
      }
    }
  }, [bookings]);

  const params = useMemo(
    () => ({ bookingsStatusFilters, page, per_page }),
    [bookingsStatusFilters, page],
  );

  useEffect(() => {
    setBookingLoading(true);
    setBookings([]);
  }, [bookingsStatusFilters]);

  const handleActiveBookingsFilterChange = (newActiveOptions: string) => {
    setBookingsStatusFilters(newActiveOptions);
  };

  const {
    isLoading: isLandLordBookingLoading,
    isFetching: isLandLordBookingFetching,
  } = useQuery({
    queryKey: ['bookings', params],
    queryFn: () => bookingsAPI.getLandlordBookings(params),
    onSuccess: (response: PaginatedResponse<Array<LandlordBooking>>) => {
      const newBookings = [...bookings, ...response.data];
      setPage(response.page);
      setBookings(newBookings as LandlordBooking[]);
      setTotalPage(response.total_pages);
      setHasMore(!(newBookings.length === response.total_items));
    },
    onSettled: () => {
      setBookingLoading(false);
    },
    onError(error: AxiosError) {
      console.log(error);
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: user?.type === 'landlord',
  });

  const {
    isLoading: isTenantBookingLoading,
    isFetching: isTenantBookingFetching,
  } = useQuery({
    queryKey: ['bookings', params],
    queryFn: () => bookingsAPI.getTenantBookings(params),
    onSuccess: (response: PaginatedResponse<Array<TenantBooking>>) => {
      const newBookings = [...bookings, ...response.data];
      setPage(response.page);
      setBookings(newBookings as TenantBooking[]);
      setTotalPage(response.total_pages);
      setHasMore(!(newBookings.length === response.total_items));
    },
    onSettled: () => {
      setBookingLoading(false);
    },
    onError(error: AxiosError) {
      console.log(error);
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: user?.type === 'tenant',
  });

  const fetchNextPage = () => {
    if (hasMore && totalPage !== page) setPage(prevValue => prevValue + 1);
  };

  const lastBookingRef = useIntersectionObserver<HTMLLIElement>(() => {
    fetchNextPage();
  }, [!isLandLordBookingLoading, !isTenantBookingLoading, hasMore]);

  if (isLoadingUser) return null;

  return (
    <MainLayout>
      <StyledTitleWrapper>
        <StyledTitle>{upperFirst(t('booking:bookings'))}</StyledTitle>
        {(isLandLordBookingLoading ||
          isTenantBookingLoading ||
          bookingsLoading) && (
          <Spinner
            foregroundColor={theme.colors.mainAlternate}
            size={20}
            strokeSize={2}
          />
        )}
      </StyledTitleWrapper>
      {user?.type === 'landlord' && (
        <Filter
          activeOptions={bookingsStatusFilters}
          options={[
            { name: 'pending', label: upperFirst(t('booking:pending')) },
            { name: 'approved', label: upperFirst(t('booking:approved')) },
            { name: 'cancelled', label: upperFirst(t('booking:cancelled')) },
          ]}
          onChange={
            handleActiveBookingsFilterChange as (x: string | string[]) => void
          }
          isMulti={false}
        />
      )}
      {!bookingsLoading && (
        <StyledListWrapper aria-live="polite">
          <Collapsible allowMultiple={false}>
            {!!bookings?.length && (
              <StyledList>
                {bookings.map((booking, index, bookings) => (
                  <BookingListItem
                    key={booking.id}
                    booking={booking}
                    setBookings={
                      setBookings as Dispatch<
                        SetStateAction<LandlordBooking[] | TenantBooking[]>
                      >
                    }
                    lastBookingRef={
                      bookings.length - 1 === index ? lastBookingRef : null
                    }
                  />
                ))}
                {(isLandLordBookingFetching || isTenantBookingFetching) && (
                  <StyledSpinner
                    foregroundColor={theme.colors.mainAlternate}
                    size={20}
                    strokeSize={2}
                  />
                )}
              </StyledList>
            )}
            {bookings.length === 0 &&
              !(isLandLordBookingLoading || isTenantBookingLoading) && (
                <StyledEmptyList>
                  {upperFirst(t('booking:noResultsFound'))}
                </StyledEmptyList>
              )}
          </Collapsible>
        </StyledListWrapper>
      )}
    </MainLayout>
  );
}
